<template>
    <div class="embedded-chat">
        <div class="chat-header">
            <div class="chat-header-icon">
                <v-icon color="white" size="20">mdi-bottle-wine</v-icon>
            </div>
            <span class="chat-header-title">Reserve Your Allocation</span>
        </div>

        <div ref="messagesContainer" class="chat-messages">
            <transition-group name="message-slide" tag="div" class="chat-messages-transition-container">
                <div
                    v-for="message in visible_messages"
                    :ref="'message-' + message.id"
                    :key="message.id"
                    :data-message-id="message.id"
                    class="chat-message"
                    :class="{
                        'is-business': message.from === 'business',
                        'is-customer': message.from === 'customer'
                    }"
                >
                    <div class="message-bubble">
                        <template v-if="message.component">
                            <component
                                :is="message.component"
                                v-bind="message.component_props"
                                @action="handleComponentAction(message, $event)"
                                @complete="handleComponentComplete(message, $event)"
                                @notify-me="handleNotifyMe(message)"
                                @download-app="handleDownloadApp"
                            />
                        </template>
                        <template v-else>
                            <span v-html="formatMessage(message.text)"></span>
                        </template>
                    </div>
                </div>
            </transition-group>

            <div v-if="is_typing" class="loading-indicator">
                <span class="loader-spinner"></span>
            </div>

            <div v-if="current_quick_replies.length > 0 && !is_typing && !is_selecting_quick_reply" class="quick-replies-container">
                <quick-reply-buttons
                    ref="quickReplies"
                    :options="current_quick_replies"
                    :freeform_placeholder="freeform_placeholder"
                    @select="handleQuickReply"
                    @freeform-submit="handleFreeformSubmit"
                />
            </div>
        </div>

        <transition name="chip-fade">
            <button
                v-if="unseen_message_count > 0"
                class="new-messages-chip"
                @click="handleNewMessagesClick"
            >
                <v-icon color="var(--brand-primary)" x-small>mdi-arrow-down</v-icon>
                {{ unseen_message_count }} new message{{ unseen_message_count > 1 ? 's' : '' }}
            </button>
        </transition>
    </div>
</template>

<script>
import QuickReplyButtons from '@/components/demos/indirect-alcohol-promo/v2/quick-reply-buttons.vue';

export default {
    name: 'EmbeddedChatInterface',

    components: {
        QuickReplyButtons,
        MockCreditCardForm: () => import('@/components/demos/indirect-alcohol-promo/v2/mock-credit-card-form.vue'),
        MockReceiptCard: () => import('@/components/demos/indirect-alcohol-promo/v2/mock-receipt-card.vue'),
        AppPromoCard: () => import('./app-promo-card.vue'),
        HandoffNotificationCard: () => import('./handoff-notification-card.vue')
    },

    props: {
        customer_data: {
            type: Object,
            required: true
        },
        wine_data: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            touched_chat: false,
            is_typing: false,
            chat_stage: 'quantity-selection',
            messages: [],
            message_id_counter: 0,
            selected_quantity: null,
            confirmed_address: false,
            typing_delay: 800,
            receipt_action_taken: false,
            is_selecting_quick_reply: false,
            unseen_messages: {},  // { messageId: true } for Vue 2 reactivity
            message_observer: null
        };
    },

    computed: {
        visible_messages() {
            return this.messages;
        },

        current_quick_replies() {
            switch (this.chat_stage) {
                case 'quantity-selection':
                    return [
                        { label: "I'll take 6!", value: 6, icon: 'mdi-bottle-wine' },
                        { label: "I'll take 4!", value: 4, icon: 'mdi-bottle-wine' },
                        { label: "I'll take 2!", value: 2, icon: 'mdi-bottle-wine' },
                        { label: 'Something else...', is_freeform: true, icon: 'mdi-pencil' }
                    ];

                case 'address-confirmation':
                    return [
                        { label: 'Yes, ship there', value: 'confirm', icon: 'mdi-check' },
                        { label: 'Use a different address', value: 'different', icon: 'mdi-map-marker' }
                    ];

                case 'receipt-options':
                    return [
                        { label: 'Text it to me', value: 'text', icon: 'mdi-message-text' },
                        { label: 'Email it to me', value: 'email', icon: 'mdi-email' },
                        { label: 'Add to Wallet', value: 'wallet', icon: 'mdi-wallet' },
                        { label: "I don't need it", value: 'dismiss', icon: 'mdi-close' }
                    ];

                default:
                    return [];
            }
        },

        freeform_placeholder() {
            if (this.chat_stage === 'quantity-selection') {
                return 'How many bottles would you like?';
            }
            return 'Type your message...';
        },

        formatted_address() {
            const addr = this.customer_data.shipping_address;
            if (!addr) return 'Address on file';
            return `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`;
        },

        order_total() {
            if (!this.selected_quantity) return '$0.00';
            const subtotal = this.selected_quantity * this.wine_data.club_price;
            const tax = subtotal * 0.0875;
            const shipping = 12.99;
            return `$${(subtotal + tax + shipping).toFixed(2)}`;
        },

        order_subtotal() {
            if (!this.selected_quantity) return '$0.00';
            return `$${(this.selected_quantity * this.wine_data.club_price).toFixed(2)}`;
        },

        unseen_message_count() {
            return Object.keys(this.unseen_messages).length;
        }
    },

    methods: {
        generateMessageId() {
            return `msg-${++this.message_id_counter}`;
        },

        formatMessage(text) {
            return text.replace(/\n/g, '<br>');
        },

        addMessage(message) {
            const msg = {
                id: this.generateMessageId(),
                ...message
            };
            this.messages.push(msg);

            // mark as unseen initially, observer will mark as seen when visible
            if (this.touched_chat) {
                this.$set(this.unseen_messages, msg.id, true);
            }

            this.$nextTick(() => {
                this.observeMessage(msg.id);
            });
        },

        async addBusinessMessage(text, options = {}) {
            this.is_typing = true;
            await this.delay(this.typing_delay);
            this.is_typing = false;

            this.addMessage({
                from: 'business',
                text,
                ...options
            });
        },

        addCustomerMessage(text) {
            this.addMessage({
                from: 'customer',
                text
            });
        },

        async addComponentMessage(component, props = {}) {
            this.is_typing = true;
            await this.delay(this.typing_delay);
            this.is_typing = false;

            this.addMessage({
                from: 'business',
                component,
                component_props: props
            });
        },

        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },

        scrollToBottom() {
            const container = this.$el.closest('.browser-content');
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        },

        initMessageObserver() {
            const container = this.$el.closest('.browser-content'); // dirty, but crawl up DOM tree to find mock phone wrapper
            if (!container) return;

            this.message_observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const messageId = entry.target.dataset.messageId;
                            if (messageId && this.unseen_messages[messageId]) {
                                this.$delete(this.unseen_messages, messageId);
                            }
                        }
                    });
                },
                {
                    root: container,
                    threshold: 0.33 // 33% visible = seen
                }
            );
        },

        observeMessage(messageId) {
            if (!this.message_observer) return;

            const refKey = `message-${messageId}`;
            const el = this.$refs[refKey];
            if (el && el[0]) {
                this.message_observer.observe(el[0]);
            }
        },

        handleNewMessagesClick() {
            this.scrollToBottom();
        },

        async handleQuickReply(option) {
            this.touched_chat = true;
            this.is_selecting_quick_reply = true;

            switch (this.chat_stage) {
                case 'quantity-selection':
                    await this.handleQuantitySelection(option.value);
                    break;

                case 'address-confirmation':
                    await this.handleAddressConfirmation(option.value);
                    break;

                case 'receipt-options':
                    await this.handleReceiptOption(option.value);
                    break;
            }

            this.is_selecting_quick_reply = false;
            this.$nextTick(this.scrollToBottom);
        },

        async handleFreeformSubmit(text) {
            this.addCustomerMessage(text);

            await this.addBusinessMessage(
                `Thanks for your message! A team member from Barrel Creek Vineyards will reach out to you shortly to help with your request.`
            );

            await this.showHandoffNotification();

            this.chat_stage = 'human-handoff';
            this.$emit('handoff', { message: text });
        },

        async handleQuantitySelection(quantity) {
            this.selected_quantity = quantity;
            this.addCustomerMessage(`I'll take ${quantity}!`);

            const total = (quantity * this.wine_data.club_price).toFixed(2);
            await this.addBusinessMessage(
                `Great choice! ${quantity} bottles of ${this.wine_data.name} — $${total}\n\nShould we ship to your address on file?\n\n📍 ${this.formatted_address}`
            );

            this.chat_stage = 'address-confirmation';
        },

        async handleAddressConfirmation(value) {
            if (value === 'confirm') {
                this.confirmed_address = true;
                this.addCustomerMessage('Yes, ship there');

                await this.addBusinessMessage(
                    `Perfect! Your total is ${this.order_total} (includes tax & shipping).\n\nPlease enter your payment details:`
                );

                await this.addComponentMessage('MockCreditCardForm', {
                    amount: this.order_total
                });

                this.chat_stage = 'payment';
            } else {
                this.addCustomerMessage('Use a different address');
                await this.addBusinessMessage(
                    `No problem! A team member will reach out shortly to update your shipping address and complete your order.`
                );

                await this.showHandoffNotification();

                this.chat_stage = 'human-handoff';
                this.$emit('handoff', { reason: 'address-change' });
            }
        },

        async handleComponentAction(message, action) {
            if (message.component === 'MockReceiptCard') {
                await this.handleReceiptOption(action);
            }
        },

        async handleComponentComplete(message, event) {
            if (message.component === 'MockCreditCardForm') {
                await this.handlePaymentComplete();
            }
        },

        async handlePaymentComplete() {
            const orderNumber = `BC${Date.now().toString().slice(-6)}`;

            await this.addComponentMessage('MockReceiptCard', {
                title: 'Order Confirmed 🍷',
                subtitle: `Thanks, ${this.customer_data.first_name}!`,
                items: [
                    {
                        quantity: this.selected_quantity,
                        name: this.wine_data.name,
                        price: this.order_subtotal
                    }
                ],
                subtotal: this.order_subtotal,
                tax: `$${(this.selected_quantity * this.wine_data.club_price * 0.0875).toFixed(2)}`,
                shipping: '$12.99',
                total: this.order_total,
                order_number: orderNumber,
                show_actions: false
            });

            await this.delay(500);
            await this.addBusinessMessage(
                `Your wine is reserved! 🍷 We'll send you a message when it's ready to ship.\n\nWould you like a copy of your receipt?`
            );

            this.chat_stage = 'receipt-options';
        },

        async handleReceiptOption(value) {
            this.receipt_action_taken = true;

            const labels = {
                wallet: 'Add to Wallet',
                text: 'Text it to me',
                email: 'Email it to me',
                dismiss: "I don't need it"
            };

            this.addCustomerMessage(labels[value] || value);

            if (value === 'dismiss') {
                await this.addBusinessMessage(`No problem!`);
            } else {
                const confirmations = {
                    wallet: `Added to your Apple Wallet!`,
                    text: `Receipt sent to ${this.customer_data.phone}!`,
                    email: `Receipt sent to your email!`
                };
                await this.addBusinessMessage(confirmations[value]);
            }

            await this.delay(300);
            await this.showAppPromo();
        },

        async showAppPromo() {
            await this.addBusinessMessage(
                `One more thing — have you tried the RedChirp app?`
            );

            await this.addComponentMessage('AppPromoCard', {
                customer_name: this.customer_data.first_name
            });

            this.chat_stage = 'complete';
            this.$emit('complete');
        },

        async showHandoffNotification() {
            await this.addComponentMessage('HandoffNotificationCard', {
                phone_number: this.customer_data.phone,
                show_app_promo: true
            });
        },

        handleNotifyMe(message) {
            this.$emit('notify-preference', { type: 'text', phone: this.customer_data.phone });
        },

        handleDownloadApp() {
            this.$emit('download-app');
        },

        initialize() {
            this.addMessage({
                from: 'business',
                text: `Hi ${this.customer_data.first_name}! 👋\n\nHow many bottles of ${this.wine_data.name} would you like to reserve?`
            });
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.initMessageObserver();
            this.initialize();
        });
    },

    beforeDestroy() {
        if (this.message_observer) {
            this.message_observer.disconnect();
        }
    }
};
</script>

<style lang="scss" scoped>
.embedded-chat {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 1rem;
    overflow: hidden;
    transition: all 0.2s ease;
}

.chat-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: var(--brand-primary);
    color: #fff;

    &-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 50%;
    }

    &-title {
        font-size: 0.9rem;
        font-weight: 600;
    }
}

.chat-messages {
    flex: 1;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &::-webkit-scrollbar {
        width: 3px;
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 3px;
        transition: background 0.2s ease;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.4);
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;

    &-transition-container {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
}

.chat-message {
    display: flex;
    max-width: 85%;

    &.is-business {
        align-self: flex-start;

        .message-bubble {
            background: var(--wine-bg);
            color: #333;
            border-bottom-left-radius: 0.25rem;
        }
    }

    &.is-customer {
        align-self: flex-end;

        .message-bubble {
            background: var(--brand-primary);
            color: #fff;
            border-bottom-right-radius: 0.25rem;
        }
    }
}

.message-bubble {
    padding: 0.75rem 1rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    line-height: 1.4;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

    // allow component messages to break out of bubble styling
    &:has(.receipt-card),
    &:has(.apple-pay-button),
    &:has(.card-payment-button),
    &:has(.credit-card-form),
    &:has(.app-promo-card),
    &:has(.handoff-notification-card) {
        padding: 0;
        background: transparent;
        box-shadow: none;
        color: inherit;
    }
}

.quick-replies-container {
    padding: 0;
    margin: 0;
}

.loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem 0;
}

.loader-spinner {
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid var(--wine-bg);
    border-top-color: var(--brand-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.message-slide {
    &-enter {
        opacity: 0;
        transform: translateY(0.5rem);
        
        &-active {
            transition: all 0.3s ease;
        }
    }

    &-leave {
        &-to {
            opacity: 0;
        }

        &-active {
            transition: all 0.2s ease;
        }
    }
}

.new-messages-chip {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0.4rem 0.75rem;
    background: #fff;
    color: var(--brand-primary);
    border: none;
    border-radius: 1rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: background 0.2s ease, box-shadow 0.2s ease;
    z-index: 10;

    .v-icon {
        margin-top: 1px;
    }

    &:hover {
        background: #fafafa;
        box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
    }

    &:active {
        transform: translateX(-50%) scale(0.98);
    }
}

.chip-fade {
    &-enter-active,
    &-leave-active {
        transition: opacity 0.25s ease, transform 0.25s ease;
    }

    &-enter,
    &-leave-to {
        opacity: 0;
        transform: translateX(-50%) translateY(0.5rem);
    }

    &-enter-to,
    &-leave {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}
</style>
